import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  HiOutlineUser,
  HiOutlineMail,
  HiOutlineLockClosed,
  HiOutlineEye,
  HiOutlineEyeOff,
  HiOutlineExclamationCircle,
  HiOutlineInformationCircle,
  HiOutlineCheck,
} from 'react-icons/hi';
import { useAuth } from '../contexts/AuthContext';

const Register = () => {
  const navigate = useNavigate();
  const { register: registerUser } = useAuth();
  const [typeCompte, setTypeCompte] = useState('apprenant');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    password: '',
    password_confirmation: '',
    conditions: false,
    formations_interet: [],
  });

  const formationsAdultes = [
    { value: 'dev_web', label: 'Développement Web' },
    { value: 'data_science', label: 'Data Science' },
    { value: 'cybersecurite', label: 'Cybersécurité' },
    { value: 'assistant', label: 'Assistant(e) et Secrétaire de direction' },
    { value: 'strategie', label: 'Stratégies marketing digital' },
    { value: 'administration', label: 'Administration et sécurité des réseaux' },
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      if (name === 'conditions') {
        setFormData({ ...formData, conditions: checked });
      } else {
        const currentList = [...formData.formations_interet];
        const updatedList = checked
          ? [...currentList, value]
          : currentList.filter((item) => item !== value);
        setFormData({ ...formData, formations_interet: updatedList });
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    if (!formData.conditions) {
      setErrors({ global: 'Vous devez accepter les conditions générales' });
      setLoading(false);
      return;
    }

    try {
      const dataToSend = {
        type: typeCompte,
        nom: formData.nom,
        email: formData.email,
        telephone: formData.telephone,
        password: formData.password,
        password_confirmation: formData.password_confirmation,
      };

      if (typeCompte === 'apprenant') {
        dataToSend.formations_interet = formData.formations_interet;
      }

      const result = await registerUser(dataToSend);
      if (result.success) {
        alert('Compte créé avec succès !');
        navigate('/login');
      }
    } catch (err) {
      setErrors(err.errors || { global: err.message || "Erreur d'inscription" });
    } finally {
      setLoading(false);
    }
  };

  // Classe commune des champs texte (sobre, focus discret)
  const inputBase =
    'w-full py-2.5 border border-slate-200 rounded-lg text-slate-800 placeholder:text-slate-400 ' +
    'focus:outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100 transition';

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-xl w-full">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {/* Fin liseré de marque, discret */}
          <div className="h-1 bg-slate-900" />

          {/* Logo et titre */}
          <div className="px-8 pt-10 text-center">
            <div className="flex justify-center mb-5">
              <img
                src="/assets/logo.png"
                alt="Programming School"
                className="h-16 w-auto"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
            <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">
              Créer un compte
            </h1>
            <p className="text-slate-500 text-sm mt-2">
              Rejoignez la plateforme d'apprentissage P.School
            </p>
          </div>

          {errors.global && (
            <div className="mx-8 mt-6 p-3 bg-red-50 border border-red-100 rounded-lg flex items-center gap-2 text-red-600">
              <HiOutlineExclamationCircle className="h-5 w-5 shrink-0" />
              <p className="text-sm font-medium">{errors.global}</p>
            </div>
          )}

          <div className="px-8 pb-10 pt-7">
            {/* SÉLECTEUR DE PROFIL — segmented control */}
            <div className="mb-7">
              <label className="block text-xs font-medium text-slate-400 uppercase tracking-wider mb-2">
                Profil
              </label>
              <div className="grid grid-cols-2 gap-1 p-1 bg-slate-100 rounded-lg">
                {[
                  { key: 'apprenant', label: 'Apprenant adulte' },
                  { key: 'parent', label: "Parent d'élève" },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setTypeCompte(opt.key)}
                    className={`py-2.5 px-4 rounded-md text-sm font-medium transition-all ${
                      typeCompte === opt.key
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Nom */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Nom complet
                </label>
                <div className="relative">
                  <HiOutlineUser className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                  <input
                    type="text"
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    className={`${inputBase} pl-10 pr-3`}
                    placeholder="Nom et prénom"
                    required
                  />
                </div>
              </div>

              {/* Email + Téléphone */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Email
                  </label>
                  <div className="relative">
                    <HiOutlineMail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`${inputBase} pl-10 pr-3`}
                      placeholder="nomprenom@gmail.com"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Téléphone
                  </label>
                  <PhoneInput
                    country={'bf'}
                    value={formData.telephone}
                    onChange={(phone) => setFormData({ ...formData, telephone: phone })}
                    inputClass="!w-full !py-2.5 !h-auto !text-slate-800 !border-slate-200 !rounded-lg"
                    buttonClass="!border-slate-200 !rounded-l-lg !bg-slate-50"
                    containerClass="!w-full"
                  />
                </div>
              </div>

              {/* Section conditionnelle */}
              {typeCompte === 'apprenant' ? (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Quelles formations vous intéressent&nbsp;?
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {formationsAdultes.map((f) => {
                      const selected = formData.formations_interet.includes(f.value);
                      return (
                        <label
                          key={f.value}
                          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-all ${
                            selected
                              ? 'border-slate-900 bg-slate-50'
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <span
                            className={`flex items-center justify-center w-4 h-4 rounded border shrink-0 transition-colors ${
                              selected
                                ? 'bg-slate-900 border-slate-900'
                                : 'bg-white border-slate-300'
                            }`}
                          >
                            {selected && <HiOutlineCheck className="w-3 h-3 text-white" />}
                          </span>
                          <input
                            type="checkbox"
                            name="formations_interet"
                            value={f.value}
                            checked={selected}
                            onChange={handleChange}
                            className="sr-only"
                          />
                          <span className="text-sm text-slate-600">{f.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 flex gap-3">
                  <HiOutlineInformationCircle className="h-5 w-5 text-slate-400 shrink-0 mt-0.5" />
                  <p className="text-xs text-slate-600 leading-relaxed">
                    En tant que <strong className="text-slate-800">parent</strong>, vous pourrez
                    inscrire vos enfants et choisir leurs formations (robotique, programmation, jeu
                    vidéo, etc.) depuis votre espace personnel après l'inscription.
                  </p>
                </div>
              )}

              {/* Mots de passe */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Mot de passe
                  </label>
                  <div className="relative">
                    <HiOutlineLockClosed className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={handleChange}
                      className={`${inputBase} pl-10 pr-10`}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? (
                        <HiOutlineEyeOff className="w-5 h-5" />
                      ) : (
                        <HiOutlineEye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Confirmation
                  </label>
                  <div className="relative">
                    <HiOutlineLockClosed className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      name="password_confirmation"
                      placeholder="••••••••"
                      value={formData.password_confirmation}
                      onChange={handleChange}
                      className={`${inputBase} pl-10 pr-10`}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showConfirmPassword ? (
                        <HiOutlineEyeOff className="w-5 h-5" />
                      ) : (
                        <HiOutlineEye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Conditions */}
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="conditions"
                  checked={formData.conditions}
                  onChange={handleChange}
                  className="mt-0.5 w-4 h-4 text-slate-900 rounded border-slate-300 focus:ring-slate-400"
                  required
                />
                <span className="text-xs text-slate-500 leading-relaxed">
                  J'accepte les{' '}
                  <Link to="/conditions" className="text-slate-800 underline hover:text-slate-900">
                    conditions générales
                  </Link>{' '}
                  et la politique de confidentialité de P.School.
                </span>
              </label>

              <button
                type="submit"
                disabled={loading}
                className={`w-full py-3 rounded-lg font-medium text-white transition-all ${
                  loading
                    ? 'bg-slate-400 cursor-not-allowed'
                    : 'bg-slate-900 hover:bg-slate-800 active:scale-[0.99]'
                }`}
              >
                {loading ? 'Création du compte…' : "S'inscrire"}
              </button>

              <p className="text-center text-sm text-slate-500">
                Déjà un compte ?{' '}
                <Link to="/login" className="font-medium text-slate-900 hover:underline">
                  Connectez-vous
                </Link>
              </p>
            </form>
          </div>
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">
          © {new Date().getFullYear()} Programming School — Ouagadougou
        </p>
      </div>
    </div>
  );
};

export default Register;
